import { faCheck, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Label, Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { type FC, memo, useEffect, useState } from 'react';

import type { Props, OptionType } from './types';

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

/**
 * @deprecated Do not use this component. Use the base components at the root of the UI folder instead.
 */
const Select: FC<Props> = ({
  disabled,
  inline = false,
  label,
  multiple = false,
  name,
  onChange,
  options,
  placeholder,
  selectedOption
}) => {
  const [selected, setSelected] = useState<OptionType | undefined>(selectedOption ?? undefined);

  useEffect(() => {
    setSelected(selectedOption ?? undefined);
  }, [selectedOption]);

  const handleChange = (value: OptionType) => {
    setSelected(value);
    onChange(value);
  };

  const disabledClassNames = disabled ? 'opacity-50 cursor-not-allowed' : '';

  return (
    <Listbox
      value={selected}
      onChange={handleChange}
      disabled={disabled}
      multiple={multiple}
      name={name}>
      <div className={inline ? 'flex min-w-0 items-center w-full' : 'min-w-0'}>
        {label && (
          <Label
            className={`block text-sm font-medium leading-6 text-gray-900 ${inline ? 'mr-1' : ''}`}>
            {label}
          </Label>
        )}

        <div className="relative w-full">
          <ListboxButton
            className={`relative w-full cursor-default rounded-md bg-white py-2 pl-3 pr-10 text-left text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:outline-hidden focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6 ${disabledClassNames}`}>
            {({ open }) => (
              <>
                {selected && selected.label ? (
                  <span className="flex min-w-0 items-center gap-2">
                    {selected.icon && (
                      <FontAwesomeIcon icon={selected.icon} className="shrink-0 text-gray-500" />
                    )}
                    <span className="shrink-0 font-medium">{selected.label}</span>
                    {selected.subLabel && (
                      <>
                        <span className="shrink-0 text-gray-400">·</span>
                        <span className="min-w-0 truncate text-gray-700">{selected.subLabel}</span>
                      </>
                    )}
                  </span>
                ) : (
                  <span className="block truncate text-gray-400">{placeholder}</span>
                )}

                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <FontAwesomeIcon
                    icon={open ? faChevronUp : faChevronDown}
                    className="h-3 w-3 text-gray-400"
                    aria-hidden="true"
                  />
                </span>
              </>
            )}
          </ListboxButton>

          <ListboxOptions
            transition
            className="absolute z-10 mt-1 max-h-60 min-w-full w-max max-w-[90vw] overflow-auto rounded-md bg-white py-2 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-hidden sm:text-sm transition ease-in duration-100 data-[closed]:opacity-0">
            {options.map((option) => (
              <ListboxOption
                key={String(option.value)}
                className={({ focus }) =>
                  classNames(
                    focus ? 'bg-indigo-600 text-white' : 'text-gray-900',
                    'relative cursor-default select-none py-2 pl-3 pr-9'
                  )
                }
                value={option}>
                {({ selected: isSelected, focus }) => (
                  <>
                    <span
                      className={classNames(
                        'flex min-w-0 items-center gap-2',
                        isSelected ? 'font-semibold' : 'font-normal'
                      )}>
                      {option.icon && (
                        <FontAwesomeIcon
                          icon={option.icon}
                          className={classNames('shrink-0', focus ? 'text-white' : 'text-gray-500')}
                        />
                      )}
                      <span className="shrink-0">{option.label}</span>
                      {option.subLabel && (
                        <>
                          <span
                            className={classNames(
                              'shrink-0',
                              focus ? 'text-white/60' : 'text-gray-400'
                            )}>
                            ·
                          </span>
                          <span
                            className={classNames(
                              'min-w-0 truncate',
                              focus ? 'text-white' : 'text-gray-700'
                            )}>
                            {option.subLabel}
                          </span>
                        </>
                      )}
                    </span>

                    {isSelected && (
                      <span
                        className={classNames(
                          focus ? 'text-white' : 'text-indigo-600',
                          'absolute inset-y-0 right-0 flex items-center pr-4'
                        )}>
                        <FontAwesomeIcon icon={faCheck} className="h-5 w-5" aria-hidden="true" />
                      </span>
                    )}
                  </>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      </div>
    </Listbox>
  );
};

export default memo(Select);
